import pegaApps from 'pegaApps.json';

import { APP_BASE_HREF, Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ApplicationRef, DoBootstrap, Injectable, NgModule, Renderer2, RendererFactory2, Type } from '@angular/core';

import { PegaChannelName, PegaFrontendFlavour } from '@ap/pega';

import { BaseStarterAppDirective } from './base-starter-app/base-starter-app.directive';
import { StarterAppMijnComponent } from './starter-app-mijn/starter-app-mijn.component';
import { StarterAppMijnModule } from './starter-app-mijn/starter-app-mijn.module';
import { StarterAppMobielComponent } from './starter-app-mobiel/starter-app-mobiel.component';
import { StarterAppMobielModule } from './starter-app-mobiel/starter-app-mobiel.module';
import { StarterAppRetailComponent } from './starter-app-retail/starter-app-retail.component';
import { StarterAppRetailModule } from './starter-app-retail/starter-app-retail.module';
import { StarterAppRetailModuleUtils } from './starter-app-retail/starter-app-retail.module.utils';

@Injectable()
export class RetailPathLocationStrategy extends PathLocationStrategy {
    public prepareExternalUrl(url: string): string {
        const baseHref = this.getBaseHref();
        const extUrl = super.prepareExternalUrl(url);
        return baseHref.endsWith('.html') || baseHref.endsWith('.html/') ? Location.stripTrailingSlash(extUrl) : extUrl;
    }
}

@NgModule({
    imports: [StarterAppMijnModule, StarterAppMobielModule, StarterAppRetailModule],
    providers: [
        {
            provide: APP_BASE_HREF,
            useValue: location.pathname
        },
        {
            provide: LocationStrategy,
            useClass: RetailPathLocationStrategy
        }
    ]
})
export class StarterAppModule implements DoBootstrap {
    protected readonly renderer: Renderer2;

    /**
     * Component to be bootstrapped for the Mijn-channel, must be a sub component of BaseStarterAppDirective.
     */
    protected readonly mijnComponent: Type<BaseStarterAppDirective> = StarterAppMijnComponent;
    /**
     * Component to be bootstrapped for the Mobiel-channel, must be a sub component of BaseStarterAppDirective.
     */
    protected readonly mobielComponent: Type<BaseStarterAppDirective> = StarterAppMobielComponent;
    /**
     * Component to be bootstrapped for the Retail-channel, must be a sub component of BaseStarterAppDirective.
     */
    protected readonly retailComponent: Type<BaseStarterAppDirective> = StarterAppRetailComponent;

    constructor(protected readonly rendererFactory: RendererFactory2) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    public ngDoBootstrap(appRef: ApplicationRef): void {
        const rootElement: HTMLElement = this.renderer.selectRootElement(pegaApps.baseApps[0].name, true);
        const pegaChannelName = this.getPegaChannelName(rootElement);

        this.bootstrapApplicationForChannel(appRef, pegaChannelName, rootElement);
    }

    protected bootstrapApplicationForChannel(appRef: ApplicationRef, pegaChannelName?: PegaChannelName, rootElement?: HTMLElement): void {
        switch (pegaChannelName) {
            case PegaChannelName.MIJN:
                appRef.bootstrap(this.mijnComponent, rootElement || 'pega-starter-app-mijn');
                break;
            case PegaChannelName.MOBIEL:
                appRef.bootstrap(this.mobielComponent, rootElement || 'pega-starter-app-mobiel');
                break;
            case PegaChannelName.RETAIL:
            case PegaChannelName.RETAIL_STANDALONE:
            default:
                appRef.bootstrap(this.retailComponent, rootElement || 'pega-starter-app-retail');
                StarterAppRetailModuleUtils.setStarterAppInputs(appRef, rootElement?.tagName?.toLowerCase() || 'pega-starter-app-retail');
                break;
        }
    }

    protected getPegaChannelName(rootElement: HTMLElement): PegaChannelName | undefined {
        const apPegaChannel = rootElement.getAttribute('apPegaChannel') as PegaChannelName;

        if (apPegaChannel) {
            return apPegaChannel;
        }

        return pegaApps.frontends
            .map(
                (frontend) =>
                    frontend.flavours.find((flavour: PegaFrontendFlavour) => location.pathname.includes(flavour.baseUrl))
                        ?.channel as PegaChannelName
            )
            .find((pegaChannelName) => !!pegaChannelName);
    }
}
